import {
  Badge,
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ProtocolBlockDto, ProtocolSectionDto } from "../../../../autogen/bff-api";
import { NarrativeField } from "./NarrativeField";

const FieldBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <HStack align="baseline" spacing="3">
    <Text fontSize="sm" color="smMuted" minW="220px">
      {block.label}
    </Text>
    <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
      {block.value || "–"}
    </Text>
  </HStack>
);

const FieldRowBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <Wrap spacingX="7" spacingY="1" py="1">
    {(block.fields ?? []).map((field, index) => (
      <WrapItem key={index}>
        <Text fontSize="sm" color="smMuted" mr="2">
          {field.label}
        </Text>
        <Text fontSize="sm" color="smText">
          {field.value || "–"}
        </Text>
      </WrapItem>
    ))}
  </Wrap>
);

const ParagraphBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <Box py="1">
    {block.label && (
      <Text fontSize="xs" color="smMuted" textTransform="uppercase" letterSpacing="0.03em" mb="1">
        {block.label}
      </Text>
    )}
    <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
      {block.value}
    </Text>
  </Box>
);

const NoteBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <Box py="1">
    {block.label && (
      <Text fontSize="sm" color="smMuted" mb="1">
        {block.label}
      </Text>
    )}
    <Text fontSize="sm" fontStyle="italic" color="smMuted" whiteSpace="pre-wrap">
      {block.value}
    </Text>
  </Box>
);

const SubheadingBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <HStack align="baseline" spacing="2" pt="2" pb="1">
    <Text fontSize="md" fontWeight="700" color="smPrimary">
      {block.value}
    </Text>
    {block.badge && (
      <Badge colorScheme="teal" variant="subtle" rounded="full" px="2" textTransform="none" fontWeight="600">
        {block.badge}
      </Badge>
    )}
    {block.label && (
      <Text fontSize="sm" color="smMuted">
        {block.label}
      </Text>
    )}
  </HStack>
);

const RankBadge = ({ value, isTop }: { value: string; isTop: boolean }) => (
  <Flex
    align="center"
    justify="center"
    boxSize="6"
    rounded="full"
    bg={isTop ? "smPrimary" : "smProtocolRowAltBg"}
    color={isTop ? "white" : "smMuted"}
    fontSize="xs"
    fontWeight="600"
  >
    {value}
  </Flex>
);

const IDENTITY_WIDTH_PX = 220;
const RANK_WIDTH_PX = 32;

const TableBlock = ({ block }: { block: ProtocolBlockDto }) => {
  const columns = block.columns ?? [];
  const rows = block.rows ?? [];
  const numericColumns = new Set(block.numericColumns ?? []);
  const emphasizeFirstRow = block.emphasizeFirstRow ?? false;

  const lastIndex = Math.max(columns.length - 1, 0);
  const edgePadding = (index: number) => ({
    pl: index === 0 ? "0" : "8px",
    pr: index === lastIndex ? "0" : "8px",
  });
  const identityColumn = columns.findIndex((_, index) => index !== block.rankColumn);
  const identityWidth = block.rankColumn == null ? IDENTITY_WIDTH_PX : IDENTITY_WIDTH_PX - RANK_WIDTH_PX;
  const columnWidth = (index: number) => {
    if (index === block.rankColumn) return `${RANK_WIDTH_PX}px`;
    if (index === identityColumn) return `${identityWidth}px`;
    return undefined;
  };

  const headerCellProps = {
    color: "smMuted",
    fontSize: "11px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.03em",
    fontWeight: "600",
    borderBottom: "1.5px solid",
    borderColor: "smBorder",
    pt: "4px",
    pb: "6px",
  };

  return (
    <Box overflowX="auto" py="1">
      <Table variant="unstyled" width="100%" sx={{ tableLayout: "fixed" }}>
        <Thead>
          <Tr>
            {columns.map((col, colIndex) => (
              <Th
                key={col}
                {...headerCellProps}
                {...edgePadding(colIndex)}
                width={columnWidth(colIndex)}
                textAlign={numericColumns.has(colIndex) ? "right" : "left"}
              >
                {col}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.length === 0 && block.emptyText ? (
            <Tr>
              <Td
                colSpan={Math.max(columns.length, 1)}
                textAlign="center"
                fontStyle="italic"
                color="smMuted"
                fontSize="sm"
                py="3"
                px="0"
              >
                {block.emptyText}
              </Td>
            </Tr>
          ) : (
            rows.map((row, rowIndex) => {
              const isWinner = emphasizeFirstRow && rowIndex === 0;
              const isLastRow = rowIndex === rows.length - 1;
              return (
                <Tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <Td
                      key={cellIndex}
                      color="smText"
                      fontWeight={isWinner ? "600" : undefined}
                      borderBottom={isLastRow ? undefined : "1px solid"}
                      borderColor="smBorder"
                      {...edgePadding(cellIndex)}
                      py="9px"
                      textAlign={numericColumns.has(cellIndex) ? "right" : "left"}
                    >
                      {cellIndex === block.rankColumn ? (
                        <RankBadge value={cell ?? ""} isTop={isWinner} />
                      ) : (
                        <Text fontSize="sm">{cell || "–"}</Text>
                      )}
                    </Td>
                  ))}
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

interface Props {
  section: ProtocolSectionDto;
  onSaveNarrative: (editableKey: string, value: string) => void;
}

export const ProtocolSectionView = ({ section, onSaveNarrative }: Props) => {
  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
      <Heading as="h2" size="sm" color="smText" mb="1">
        {section.number}. {section.heading}
      </Heading>
      <Divider mb="3" borderColor="smBorder" />
      <Stack spacing="3">
        {section.blocks.map((block, index) => {
          if (block.kind === "paragraph" && block.editableKey) {
            return (
              <Box key={index} py="1">
                {block.label && (
                  <Text fontSize="sm" color="smMuted" mb="1">
                    {block.label}
                  </Text>
                )}
                <NarrativeField
                  initialValue={block.value ?? ""}
                  onSave={(value) => onSaveNarrative(block.editableKey as string, value)}
                />
              </Box>
            );
          }
          if (block.kind === "paragraph") return <ParagraphBlock key={index} block={block} />;
          if (block.kind === "note") return <NoteBlock key={index} block={block} />;
          if (block.kind === "subheading") return <SubheadingBlock key={index} block={block} />;
          if (block.kind === "table") return <TableBlock key={index} block={block} />;
          if (block.kind === "divider") {
            return (
              <Box key={index} py="2.5">
                <Divider borderColor="smBorder" />
              </Box>
            );
          }
          if (block.kind === "fieldRow") return <FieldRowBlock key={index} block={block} />;
          return <FieldBlock key={index} block={block} />;
        })}
      </Stack>
    </Box>
  );
};
